import { ShieldCheckIcon, KeyIcon } from '@heroicons/react/24/outline';
import { Card, CardBody, CardHeader, Chip, Button } from '@nextui-org/react';
import React, { useState } from 'react';

import { TypographyUI } from '@portal/components/common/Typography.ui';
import { useEncryptionStatus } from '@portal/utils/useEncryptionStatus';
import { KeyGenerationButton, KeyGenerationModal } from '@portal/views/encryption/KeyGeneration';

import { KeyRecoveryModal } from './KeyRecoveryModal';

/**
 * Encryption Status Component
 *
 * Displays the current encryption status and provides dynamic setup/recovery options
 */
export const EncryptionStatus = () => {
  const { encryptionStatus, isLoading, forceRefetch } = useEncryptionStatus();
  const [isGenerationModalOpen, setIsGenerationModalOpen] = useState(false);
  const [isRecoveryModalOpen, setIsRecoveryModalOpen] = useState(false);

  const handleSuccess = async () => {
    await forceRefetch();
  };

  if (isLoading) {
    return (
      <div className='flex w-full h-[calc(100vh-200px)] gap-4 items-center justify-center'>
        <div className='flex items-center gap-8'>
          <TypographyUI variant='h5' color='blue-gray'>
            Loading encryption status...
          </TypographyUI>
        </div>
      </div>
    );
  }

  const isReady = encryptionStatus?.status === 'ready';
  const isMissing = encryptionStatus?.status === 'missing';
  const isMissingWithData = encryptionStatus?.status === 'missing_with_data';
  const isSystemChanged = encryptionStatus?.status === 'system_changed';
  const isInvalid = encryptionStatus?.status === 'invalid';

  return (
    <div className='flex w-full h-[calc(100vh-32px)] gap-3 items-center justify-center'>
      <div className='w-full max-w-2xl space-y-4'>
        {/* Status Card */}
        <Card className='w-full' shadow='lg'>
          <CardHeader className='flex-col items-start gap-1 pb-3 px-4'>
            <div className='flex items-center gap-2'>
              <ShieldCheckIcon className={`h-5 w-5 ${isReady ? 'text-green-500' : 'text-orange-500'}`} />
              <TypographyUI variant='h5' color='blue-gray' className='font-semibold'>
                Encryption Status
              </TypographyUI>
            </div>
            <TypographyUI variant='body' color='gray' className='text-sm'>
              Current state of data encryption on your system
            </TypographyUI>
          </CardHeader>
          <CardBody className='px-4 pb-4'>
            <div className='space-y-3'>
              {/* Status Display */}
              <div className='flex items-center justify-between p-3 pt-0 bg-gray-50 rounded-lg'>
                <div className='flex items-center gap-2'>
                  <div
                    className={`w-2.5 h-2.5 rounded-full ${
                      isReady
                        ? 'bg-green-500'
                        : isSystemChanged || isMissingWithData
                          ? 'bg-orange-500'
                          : 'bg-yellow-500'
                    }`}
                  />
                  <TypographyUI variant='body' color='gray' className='font-medium'>
                    {isReady
                      ? 'Encryption Ready'
                      : isSystemChanged
                        ? 'System Changed - Recovery Required'
                        : isMissingWithData
                          ? 'Missing Key File - Recovery Required'
                          : 'Encryption Not Ready'}
                  </TypographyUI>
                </div>
                <div
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isReady
                      ? 'bg-green-100 text-green-800'
                      : isSystemChanged || isMissingWithData
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {isReady ? 'Active' : isSystemChanged || isMissingWithData ? 'Recovery Required' : 'Inactive'}
                </div>
              </div>

              {/* Status Details */}
              <div
                className={`p-3 border rounded-lg ${
                  isReady
                    ? 'bg-blue-50 border-blue-200'
                    : isSystemChanged || isMissingWithData
                      ? 'bg-orange-50 border-orange-200'
                      : 'bg-blue-50 border-blue-200'
                }`}
              >
                <TypographyUI
                  variant='body'
                  color={isSystemChanged || isMissingWithData ? 'orange' : 'blue-gray'}
                  className='font-medium mb-1'
                >
                  Status Details
                </TypographyUI>
                <TypographyUI variant='body' color='gray' className='text-sm'>
                  {isReady
                    ? 'Your system data is encrypted and secure. All sensitive information is protected using AES-256 encryption.'
                    : isSystemChanged
                      ? 'Your system has changed since encryption was set up. You need to recover your encryption key using your recovery code and master password. Common causes: hardware changes, OS updates, virtualization changes, or system reconfiguration.'
                      : isMissingWithData
                        ? 'Your encryption key file is missing but encrypted data exists in the database. You need to recover your encryption key using your recovery code and master password to access your data.'
                        : 'Your system data is not encrypted. Generate an encryption key to secure sensitive information like passwords and configuration data.'}
                </TypographyUI>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Action Card - Dynamic based on status */}
        {!isReady && (
          <Card className='w-full' shadow='lg'>
            <CardHeader className='flex-col items-start gap-1 pb-3 px-4'>
              <TypographyUI variant='h5' color='blue-gray' className='font-semibold'>
                {isSystemChanged || isMissingWithData ? 'Recover Encryption Key' : 'Setup Encryption'}
              </TypographyUI>
              <TypographyUI variant='body' color='gray' className='text-sm'>
                {isSystemChanged || isMissingWithData
                  ? 'Enter your recovery code and master password to restore access'
                  : 'Generate an encryption key to secure your system data'}
              </TypographyUI>
            </CardHeader>
            <CardBody className='px-4 pb-4'>
              <div className='flex justify-center'>
                {isSystemChanged || isMissingWithData ? (
                  <Button color='primary' variant='shadow' size='lg' onPress={() => setIsRecoveryModalOpen(true)}>
                    <KeyIcon className='h-5 w-5 mr-2' />
                    Recover Encryption Key
                  </Button>
                ) : (
                  <KeyGenerationButton onOpen={() => setIsGenerationModalOpen(true)} />
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Technical Details Card */}
        {encryptionStatus?.details && (
          <Card className='w-full' shadow='lg'>
            <CardHeader className='flex-col items-start gap-1 pb-3 px-4'>
              <TypographyUI variant='h5' color='blue-gray' className='font-semibold'>
                Technical Details
              </TypographyUI>
              <TypographyUI variant='body' color='gray' className='text-sm'>
                Detailed information about encryption components
              </TypographyUI>
            </CardHeader>
            <CardBody className='px-4 pb-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                {/* Key File Status */}
                <div className='p-3 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-center justify-between mb-1'>
                    <TypographyUI variant='body' className='font-semibold text-gray-700 text-sm'>
                      Key File
                    </TypographyUI>
                    <Chip
                      size='sm'
                      color={encryptionStatus.details.keyFileExists ? 'success' : 'danger'}
                      variant='flat'
                    >
                      {encryptionStatus.details.keyFileExists ? 'Present' : 'Missing'}
                    </Chip>
                  </div>
                  <TypographyUI variant='body' className='text-xs text-gray-600'>
                    {encryptionStatus.details.keyFileExists
                      ? 'Encryption key file is available on the system'
                      : 'No encryption key file found on the system'}
                  </TypographyUI>
                </div>

                {/* System Fingerprint */}
                <div className='p-3 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-center justify-between mb-1'>
                    <TypographyUI variant='body' className='font-semibold text-gray-700 text-sm'>
                      System Fingerprint
                    </TypographyUI>
                    <Chip
                      size='sm'
                      color={encryptionStatus.details.systemFingerprintValid ? 'success' : 'danger'}
                      variant='flat'
                    >
                      {encryptionStatus.details.systemFingerprintValid ? 'Valid' : 'Invalid'}
                    </Chip>
                  </div>
                  <TypographyUI variant='body' className='text-xs text-gray-600'>
                    {encryptionStatus.details.systemFingerprintValid
                      ? 'System matches the stored fingerprint'
                      : 'System has changed since key creation'}
                  </TypographyUI>
                </div>

                {/* Application Key */}
                <div className='p-3 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-center justify-between mb-1'>
                    <TypographyUI variant='body' className='font-semibold text-gray-700 text-sm'>
                      Application Key
                    </TypographyUI>
                    <Chip
                      size='sm'
                      color={encryptionStatus.details.applicationKeyValid ? 'success' : 'danger'}
                      variant='flat'
                    >
                      {encryptionStatus.details.applicationKeyValid ? 'Valid' : 'Invalid'}
                    </Chip>
                  </div>
                  <TypographyUI variant='body' className='text-xs text-gray-600'>
                    {encryptionStatus.details.applicationKeyValid
                      ? 'Application encryption key is properly loaded'
                      : 'Application key validation failed'}
                  </TypographyUI>
                </div>

                {/* Last Validation */}
                <div className='p-3 bg-gray-50 rounded-lg border border-gray-200'>
                  <div className='flex items-center justify-between mb-1'>
                    <TypographyUI variant='body' className='font-semibold text-gray-700 text-sm'>
                      Last Checked
                    </TypographyUI>
                    <Chip size='sm' color='primary' variant='flat'>
                      Recent
                    </Chip>
                  </div>
                  <TypographyUI variant='body' className='text-xs text-gray-600'>
                    {new Date(encryptionStatus.details.lastValidation).toLocaleString()}
                  </TypographyUI>
                </div>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Mount both modal components */}
        <KeyGenerationModal isOpen={isGenerationModalOpen} onClose={() => setIsGenerationModalOpen(false)} />
        <KeyRecoveryModal
          isOpen={isRecoveryModalOpen}
          onClose={() => setIsRecoveryModalOpen(false)}
          onSuccess={handleSuccess}
        />
      </div>
    </div>
  );
};
